import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Col, Row, Card, CardBody, CardTitle, Spinner, Alert, Table, Input, Label } from "reactstrap";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import { apiJson } from "../../../helpers/apiConfig";

/**
 * Etkileşim Raporu — mağaza/satıcı profili açılışı, kategori açılışı ve satıcıyı
 * arama / WhatsApp / mesaj butonları (mobil + web).
 *
 * Veri: api/Etkilesim/Ozet (admin) → ETKILESIM_LOGLARI. Tablo 29.09.2026'da eklendi;
 * sayılar ancak API, web ve yeni mobil sürüm yayına alındıktan sonra birikir.
 * Eski mobil sürümler bu sayaçları göndermez — ilk haftalarda rakamlar düşük görünür.
 */

const TIPLER = [
  { key: "MAGAZA_GORUNTULEME", ad: "Mağaza / satıcı profili açılışı", ikon: "bx bx-show" },
  { key: "KATEGORI_GORUNTULEME", ad: "Kategori açılışı", ikon: "bx bx-bar-chart-alt-2" },
  { key: "TELEFON", ad: "Satıcıyı arama", ikon: "bx bx-phone-call" },
  { key: "WHATSAPP", ad: "WhatsApp'tan yazma", ikon: "bx bxl-whatsapp" },
  { key: "MESAJ", ad: "Uygulama içi mesaj", ikon: "bx bx-message-rounded-dots" },
];

const PLATFORM_AD = { ios: "iOS", android: "Android", web: "Web", "?": "Bilinmiyor" };

const sayi = (v) => Number(v || 0).toLocaleString("tr-TR");

const SiraliTablo = ({ baslik, satirlar, sutun }) => (
  <Card className="h-100">
    <CardBody>
      <CardTitle className="mb-3">{baslik}</CardTitle>
      {satirlar && satirlar.length ? (
        <div className="table-responsive">
          <Table className="table-sm align-middle mb-0">
            <thead>
              <tr>
                <th>#</th>
                <th>{sutun}</th>
                <th className="text-end">Adet</th>
              </tr>
            </thead>
            <tbody>
              {satirlar.map((s, i) => (
                <tr key={s.HEDEF_ID}>
                  <td>{i + 1}</td>
                  <td>
                    {s.AD || "—"} <span className="text-muted small">#{s.HEDEF_ID}</span>
                  </td>
                  <td className="text-end">{sayi(s.ADET)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      ) : (
        <p className="text-muted mb-0">Bu dönemde kayıt yok.</p>
      )}
    </CardBody>
  </Card>
);

const EtkilesimRaporu = () => {
  document.title = "Etkileşim Raporu | HarmanaGel Admin";

  const [gun, setGun] = useState(30);
  const [veri, setVeri] = useState(null);
  const [yukleniyor, setYukleniyor] = useState(false);
  const [hata, setHata] = useState(null);

  const yukle = useCallback(async () => {
    setYukleniyor(true);
    setHata(null);
    try {
      setVeri(await apiJson(`/api/Etkilesim/Ozet?gun=${gun}`));
    } catch (e) {
      setHata(
        e.status === 403
          ? "Bu rapor yalnızca yöneticilere açık."
          : `Rapor alınamadı: ${e.message}. ETKILESIM_LOGLARI.sql çalıştırıldı ve API güncellendi mi?`
      );
    } finally {
      setYukleniyor(false);
    }
  }, [gun]);

  useEffect(() => {
    yukle();
  }, [yukle]);

  const tipMap = useMemo(() => {
    const m = {};
    (veri?.tipler || []).forEach((t) => (m[t.TIP] = t));
    return m;
  }, [veri]);

  const platformMap = useMemo(() => {
    const m = {};
    (veri?.platformlar || []).forEach((p) => {
      m[p.TIP] = m[p.TIP] || {};
      m[p.TIP][p.PLATFORM] = p.ADET;
    });
    return m;
  }, [veri]);

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="Yönetim" breadcrumbItem="Etkileşim Raporu" />

          <Row className="mb-3 align-items-end">
            <Col xs="auto">
              <Label for="etkilesim-gun" className="mb-1">Dönem</Label>
              <Input
                id="etkilesim-gun"
                type="select"
                value={gun}
                onChange={(e) => setGun(Number(e.target.value))}
              >
                <option value={1}>Son 24 saat</option>
                <option value={7}>Son 7 gün</option>
                <option value={30}>Son 30 gün</option>
                <option value={90}>Son 90 gün</option>
                <option value={3650}>Tüm zamanlar</option>
              </Input>
            </Col>
            <Col>{yukleniyor ? <Spinner size="sm" /> : null}</Col>
          </Row>

          {hata ? <Alert color="danger">{hata}</Alert> : null}

          <Row>
            {TIPLER.map((t) => {
              const s = tipMap[t.key];
              const p = platformMap[t.key] || {};
              return (
                <Col key={t.key} xl={true} md={4} sm={6} className="mb-3">
                  <Card className="mini-stats-wid h-100">
                    <CardBody>
                      <div className="d-flex">
                        <div className="flex-grow-1">
                          <p className="text-muted fw-medium mb-2">{t.ad}</p>
                          <h4 className="mb-0">{sayi(s?.ADET)}</h4>
                          <p className="text-muted mb-0 mt-2 small">
                            {sayi(s?.TEKIL_KULLANICI)} giriş yapmış kişi
                          </p>
                          <p className="text-muted mb-0 small">
                            {Object.keys(p).length
                              ? Object.entries(p)
                                  .map(([k, v]) => `${PLATFORM_AD[k] || k} ${sayi(v)}`)
                                  .join(" · ")
                              : "—"}
                          </p>
                        </div>
                        <div className="avatar-sm rounded-circle bg-primary align-self-center mini-stat-icon">
                          <span className="avatar-title rounded-circle bg-primary">
                            <i className={`${t.ikon} font-size-24`}></i>
                          </span>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              );
            })}
          </Row>

          <Row>
            <Col lg={4} className="mb-3">
              <SiraliTablo baslik="En çok görüntülenen mağazalar" sutun="Mağaza / satıcı" satirlar={veri?.enCokGoruntulenenMagazalar} />
            </Col>
            <Col lg={4} className="mb-3">
              <SiraliTablo baslik="En çok açılan kategoriler" sutun="Kategori" satirlar={veri?.enCokAcilanKategoriler} />
            </Col>
            <Col lg={4} className="mb-3">
              <SiraliTablo baslik="En çok iletişim alan satıcılar" sutun="Satıcı" satirlar={veri?.enCokIletisimAlanSaticilar} />
            </Col>
          </Row>

          <p className="text-muted small">
            Aynı cihazdan aynı hedefe 60 saniye içinde gelen tekrarlar ve satıcının kendi profilini açması sayılmaz.
            Eski mobil sürümler bu sayaçları göndermez.
          </p>
        </div>
      </div>
    </React.Fragment>
  );
};

export default EtkilesimRaporu;
